import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { SelectionChangedEvent } from 'ag-grid-community';
import { DrRequestInfo } from '../../models/dr-request-info';
import { Endpoint } from '../../models/enums';
import { CellTemplateType, GenericColDef, GridRowSelectionType } from '../../models/grid-context';
import { RestService } from '../../services/rest/rest.service';
import { GenericGridComponent } from '../generic-grid/generic-grid.component';
import { GenericTreeNode } from '../generic-tree/generic-tree-node';
import { GenericTreeComponent } from '../generic-tree/generic-tree.component';

@Component({
    standalone: false,
    selector: 'res-data-area-and-tree',
    templateUrl: './data-area-and-tree.component.html',
    styleUrls: ['./data-area-and-tree.component.scss']
})
export class DataAreaAndTreeComponent implements OnInit {
    @ViewChild(GenericGridComponent, { static: true }) genericGrid: GenericGridComponent;
    @ViewChild(GenericTreeComponent) genericTree: GenericTreeComponent;

    selectedDataArea: any;
    nodes = new Array<GenericTreeNode>();
    selectedPath: any;
    isPathsBusy = false;
    pathHeader = 'Folder';

    @Input() automationId: string;
    @Input() isExportMode = false;
    @Input() autoSelectFirstDA = false;
    @Output() onDAATChange = new EventEmitter();

    columnDefs: Array<GenericColDef>;
    rowSelection = GridRowSelectionType.single;
    requestInfo: DrRequestInfo;

    responseArrayCallback = (value: any) => {
        value.iconClass = 'fa-database color-blue-01';
        return value;
    }

    loadDataFinishedCallback = () => {
        if (this.isExportMode && this.autoSelectFirstDA) {
            // select first item from export data area grid
            const item = this.genericGrid.gridApi?.getDisplayedRowAtIndex(0);
            if (item) {
                this.genericGrid.selectNode(item);
            }
        }
    }

    onSelectionChanged = (event?: SelectionChangedEvent) => {
        if (event && event.api) {
            this.selectedDataArea = event.api.getSelectedRows()[0];
            this.onDataAreaAndTreeChange(null);
            this.nodes = [];
            if (this.selectedDataArea) {
                const path = this.selectedDataArea.path ? this.selectedDataArea.path : '/';
                this.loadPaths(path);
            }
        }
    }

    constructor(private restService: RestService) { }

    ngOnInit(): void {
        this.requestInfo = new DrRequestInfo({
            endpoint: this.isExportMode ? Endpoint.orgManager_listExportDataAreas : Endpoint.orgManager_listProjectDataAreas,
            responseName: this.isExportMode ? 'exportDataAreas' : 'projectDataAreas',
            data: {}
        });
        if (this.isExportMode) {
            this.pathHeader = 'Folder';
        }
        this.columnDefs = new Array<GenericColDef>(
            {
                field: 'name',
                headerName: this.isExportMode ? 'Export Data Area' : 'Project Data Area',
                automationId: this.automationId,
                cellTemplateType: CellTemplateType.nameIconLabel,
                headerRequired: true,
                lockVisible: true
            }
        );
    }

    loadNodeDataCallback = (node: GenericTreeNode) => {
        if (node) {
            this.loadPaths(node.data.handle, node);
        }
    }

    selectedNodeChangedCallback = (node: GenericTreeNode) => {
        if (node) {
            this.onDataAreaAndTreeChange(node.data);
        }
    }

    loadPaths(path: string, parent?: GenericTreeNode): void {
        if (path) {
            if (!path.startsWith('/')) {
                path = '/' + path;
            }
            const connectorFolderPath = { handle: path, name: '', type: 'UNKNOWN', leaf: false };

            if (parent) {
                parent.isLoading = true;
            } else {
                this.isPathsBusy = true;
            }
            this.restService.post(Endpoint.connectorManager_exploreConnector, { connectorHandle: this.selectedDataArea.connectorHandle, parentPath: connectorFolderPath }).then(response => {
                if (parent) {
                    parent.isLoading = false;
                } else {
                    this.isPathsBusy = false;
                }
                if (response && response.paths) {
                    response.paths.forEach(element => {
                        if (element.name !== '.' && element.name !== '..') {
                            if (parent) {
                                parent.children.push(new GenericTreeNode({
                                    name: element.name,
                                    icon: 'fa-folder',
                                    iconExpanded: 'fa-folder-open',
                                    iconColor: 'color-yellow-07',
                                    isExpandable: !element.leaf,
                                    isExpandedOnSelect: !element.leaf,
                                    hasExpanderPadding: true,
                                    data: element
                                }));
                            } else {
                                this.nodes.push(new GenericTreeNode({
                                    name: element.name,
                                    icon: 'fa-folder',
                                    iconExpanded: 'fa-folder-open',
                                    iconColor: 'color-yellow-07',
                                    isExpandable: !element.leaf,
                                    isExpandedOnSelect: !element.leaf,
                                    hasExpanderPadding: true,
                                    data: element
                                }));
                            }
                        }
                    });
                    if (parent && parent.children.length === 0) {
                        parent.isExpandable = false;
                        parent.iconExpanded = null;
                    }
                }
                this.genericTree.setData(this.nodes);
            }, error => {
                if (parent) {
                    parent.isLoading = false;
                } else {
                    this.isPathsBusy = false;
                }
            });
        }
    }

    onDataAreaAndTreeChange(selPath: any): void {
        this.selectedPath = selPath;
        this.onDAATChange.emit();
    }
}
